import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Admin: draft the explainer and FAQs for a topic page with AI. */
export const draftTopicPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { geminiGenerate } = await import("./ai.server");

    await assertAdmin(context.supabase, context.userId);
    if (!data.slug || typeof data.slug !== "string") throw new Error("slug is required");

    const { data: topic, error: topicErr } = await supabaseAdmin
      .from("topic_pages")
      .select("slug,title,tag,explainer")
      .eq("slug", data.slug)
      .maybeSingle();
    if (topicErr || !topic) throw new Error(`Topic not found: ${data.slug}`);

    // Ground the draft in recent coverage.
    const { data: recent } = await supabaseAdmin
      .from("articles")
      .select("title")
      .eq("is_published", true)
      .overlaps("topic_tags", [topic.tag])
      .order("published_at", { ascending: false })
      .limit(25);
    const headlines = (recent || []).map((r) => r.title).join("\n");

    const prompt = `You are an editor at Byteline, a technology and markets newsroom. Write a guide section for the topic page "${topic.title}".

Rules:
- Plain English for a smart general reader, no jargon without a one-line explanation.
- No em dashes anywhere.
- No hype, no invented statistics, no company claims you cannot support.
- Explainer: exactly 3 paragraphs, 3-4 sentences each, separated by a blank line. Paragraph 1: what the field is and why it matters now. Paragraph 2: how the field is structured or how it works. Paragraph 3: the questions or forces driving the news right now.
- FAQs: exactly 4 objects with "q" (a common beginner question) and "a" (a 1-2 sentence answer).

Recent Byteline headlines in this topic, for context only:
${headlines}

Return ONLY valid JSON: {"explainer": "...", "faqs": [{"q": "...", "a": "..."}]}`;

    const text = await geminiGenerate(prompt, {
      temperature: 0.4,
      responseMimeType: "application/json",
    });
    if (!text) throw new Error("Gemini did not return a draft");

    const parsed = JSON.parse(text);
    const explainer = String(parsed.explainer || "").replace(/\u2014/g, "-").trim();
    const faqs = Array.isArray(parsed.faqs)
      ? parsed.faqs
          .filter((f: { q?: string; a?: string }) => f?.q && f?.a)
          .map((f: { q: string; a: string }) => ({
            q: String(f.q).replace(/\u2014/g, "-"),
            a: String(f.a).replace(/\u2014/g, "-"),
          }))
      : [];
    if (!explainer || faqs.length === 0) throw new Error("Gemini returned an incomplete draft");

    const { error: updateErr } = await supabaseAdmin
      .from("topic_pages")
      .update({ explainer, faqs, updated_at: new Date().toISOString() })
      .eq("slug", data.slug);
    if (updateErr) throw updateErr;

    return { ok: true, slug: data.slug };
  });
